import { window } from 'global';
import logger from './logger';
import {
  ROUTER_BASENAME,
  AA_EVENT_START,
  AA_EVENT_CONTINUE,
  AA_EVENT_COMPLETE,
  AA_EVENT_REVISIT,
  AA_SESSION_KEY_COURSE_CONTINUE,
  AA_SESSION_KEY_COURSE_REVISIT,
  AA_SESSION_KEY_MODULE_CONTINUE,
  AA_SESSION_KEY_MODULE_REVISIT,
  LRS_VERB_STARTED,
  LRS_VERB_COMPLETED, LEARNING_MODE_COURSEWARE,
  PAGETYPE_CURATED,
} from './constants';
import {
  getEnvironmentConstant, generateLRSID, LRSHasVerbRecordForID, unitHasCompleteRecord,
} from './utilities';
import {
  generateTutorialInfo,
  generateCuratedListInfo,
  hasCompletedRecord,
  hasConsumedRecord,
  hasStartedRecord,
} from './userUtils';
import { setChildProperty } from './object';
import {
  dispatchCJSCustomEvent, getCjsCustomEventForTriggerManualLinkClick, getPageContext, getCjsCustomEventForTriggerGenericPageView, getCjsLinkClickEventForSearchResult, getCjsLearnOneEventForSearchResult, getCjsSearchEvent, getCjsLearnOneEventForCourseStatusChange, getCjsLearnOneEventForModuleStatusChange, getCjsErrorContextForErrorBanner, getCjsLinkClickEventForSearchBar, getCjsNotificationEventForNotificationImpression, getCjsLearnOneEventForContentImpression, getCjsLearnOneEventForProblemResult, getCjsLearnOneEventForUnitComplete, getCjsLearnOneEventForCoursePageView, getCjsLearnOneEventForModulePageView, getCjsLearnOneEventForUnitConsume, getCjsLearnOneEventForLearningPlanAction, getCjsInventorBannerCtaClick, getCjsInventorExperimentExposure, getCjsInventorExperimentContext, getCjsCtaBannerClick, getContentContext, getContentContextFromRecord, getContentVersionFromRecord, mergeCjsEventContexts,
} from './cjsCustomEvents';
import { isFastTrackLearningPlan } from './fastTrackUtils';

export const formatString = (string) => (string ? string.replace(/[:]+/g, '-').toLowerCase() : string);

const resolveModuleContentRecord = ({ courseData, moduleData, moduleId }) => (
  moduleData || courseData?.tutorials?.find((tutorial) => tutorial.id === moduleId)
);

const resolveUnitContentRecord = ({ courseData, moduleData, unitId }) => {
  const outline = courseData || moduleData;
  const unit = unitId && outline?.unitLookup?.[unitId];
  if (!unit) {
    return undefined;
  }
  return unit.id != null ? unit : { ...unit, id: unitId };
};

const resolveContentViewedRecord = ({
  tutorialData, curatedData, courseData, moduleData, moduleId,
}) => {
  if (tutorialData && Object.keys(tutorialData).length) {
    return tutorialData;
  }
  if (curatedData && Object.keys(curatedData).length) {
    return curatedData;
  }
  if (courseData) {
    return courseData;
  }
  return resolveModuleContentRecord({ courseData, moduleData, moduleId: moduleId || moduleData?.id });
};

export const buildLearnEventContexts = ({
  record, env, pageCategory, event,
}) => mergeCjsEventContexts(
  getPageContext(event, pageCategory),
  (record && env) ? getContentContextFromRecord({ record, env }) : {},
);

const getLearningStatus = ({ learningRecords, id, env }) => {
  if (!learningRecords || !env) {
    return 'not-started';
  }

  if (hasCompletedRecord({ learningRecords, id, env })) {
    return 'completed';
  } if (hasConsumedRecord({ learningRecords, id, env })) {
    return 'consumed';
  } if (hasStartedRecord({ learningRecords, id, env })) {
    return 'started';
  }

  return 'not-started';
};

export const getSessionAnalyticsValues = (key) => {
  if (window.sessionStorage) {
    return JSON.parse(window.sessionStorage.getItem(key)) || [];
  }
  return [];
};

export const sessionAnalyticsHasValue = ({ key, value }) => (getSessionAnalyticsValues(key).includes(value));

export const addSessionAnalyticsValue = ({ key, value }) => {
  if (window.sessionStorage) {
    const currentVals = JSON.parse(window.sessionStorage.getItem(key)) || [];
    if (!currentVals.includes(value)) {
      window.sessionStorage.setItem(key, JSON.stringify([...currentVals, value]));
    }
  }
};

export const pushEvent = ({ event }) => {
  // eslint-disable-next-line no-underscore-dangle
  window.__analyticsChangeContext = window.__analyticsChangeContext || [];

  // eslint-disable-next-line no-underscore-dangle
  window.__analyticsChangeContext.push(event);

  // Move logging to after sending the AA event
  let stringifiedEvent = '';
  try {
    stringifiedEvent = JSON.stringify(event);
  } catch (e) {
    stringifiedEvent = 'Failed to stringify event';
  }
  // eslint-disable-next-line no-underscore-dangle
  logger.info('Logged analytics event: ', stringifiedEvent);
};

export const triggerManualLinkClick = ({
  linkSection = 'content', linkTitle, cssSelector, contentId, contentVersion, env,
}) => {
  const event = {
    eventData: {
      eventName: 'link_click',
    },
    link: {
      linkSection,
      linkTitle,
    },
  };

  if (cssSelector) {
    event.eventData.values = {
      ...event.eventData.values,
      cssSelector,
    };
  }

  const eventContexts = (contentId && env)
    ? getContentContext({ contentId, env, versionId: contentVersion })
    : null;

  dispatchCJSCustomEvent({
    eventData: getCjsCustomEventForTriggerManualLinkClick(event),
    eventName: 'link_click',
    version: '1-0-0',
    eventContexts,
  });
  pushEvent({ event });
};

export const triggerGenericPageView = ({
  pageName, tutorialData, curatedData, learningRecords, env, eventName, status, curPageType,
}) => {
  if (pageName) {
    setChildProperty(`adsk:en:learn:${pageName}`, 'digitalData.page.pageName', window);
  }
  let event = {
    eventData: {
      eventName: eventName || 'new_page_view',
    },
  };

  if (!eventName) { // tutorial/curatedTutorial/curatedList/collection page-view
    event.eventData.values = {
      pageName: window.digitalData.page.pageName,
    };
  }
  if (tutorialData && Object.keys(tutorialData).length) {
    const tutorialInfo = generateTutorialInfo({
      tutorialData, learningRecords, env, eventName,
    });
    if (tutorialInfo.values) {
      event.eventData.values = { ...event.eventData.values, ...tutorialInfo.values };
    }
    event = { ...event, ...{ tutorialInfo: tutorialInfo.tutorialInfo } };
  }

  if (curatedData && Object.keys(curatedData).length) {
    const curPage = curPageType === PAGETYPE_CURATED ? 'curatedListInfo' : 'courseInfo';
    const curatedListInfo = generateCuratedListInfo({
      pageName: window.digitalData.page.pageName, curatedData, learningRecords, env, eventName, status,
    });
    if (curatedListInfo.values) {
      event.eventData.values = curatedListInfo.values;
      event.eventData.values = { ...event.eventData.values, ...curatedListInfo.values };
    }
    event = { ...event, ...{ [curPage]: curatedListInfo.curatedListInfo } };
  }
  let pageCategory = 'generic';
  if (tutorialData && Object.keys(tutorialData).length) {
    pageCategory = 'tutorial';
  } else if (curatedData && Object.keys(curatedData).length) {
    pageCategory = curPageType === PAGETYPE_CURATED ? 'curated-list' : 'course';
  }

  dispatchCJSCustomEvent({
    eventData: getCjsCustomEventForTriggerGenericPageView(event),
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({
      record: resolveContentViewedRecord({ tutorialData, curatedData }),
      env,
      pageCategory,
      event,
    }),
  });
  pushEvent({ event });
};

const getAnalyticsObjectType = ({ data }) => {
  switch (data?.objectType?.toLowerCase().replace(' ', '')) {
  case 'module':
    if (data.learningMode === LEARNING_MODE_COURSEWARE) {
      return 'module';
    }
    return 'tutorial';

  case 'course':
    if (data.learningMode === LEARNING_MODE_COURSEWARE) {
      return 'course';
    }
    return 'curated list';

  case 'learningpathway':
    return 'collection';

  default:
    return 'na';
  }
};

export const generateCourseDataAnalyticsString = ({ data, learningRecords, env }) => {
  let products = [];
  if (data?.products) {
    products = data.products;
  } else if (data?.meta?.products) {
    products = data.meta.products;
  }
  const optional = [];
  if (learningRecords && env) {
    if (hasCompletedRecord({ learningRecords, id: data.id, env })) {
      optional.push('completed');
    } else if (hasConsumedRecord({ learningRecords, id: data.id, env })) {
      optional.push('consumed');
    } else if (hasStartedRecord({ learningRecords, id: data.id, env })) {
      optional.push('started');
    } else {
      optional.push('not started');
    }

    if (data.learningMode === LEARNING_MODE_COURSEWARE) {
      if (data.meta?.type) {
        optional.push(data.meta.type);
      } else {
        optional.push('na');
      }
      if (data.meta?.level) {
        optional.push(data.meta.level);
      } else {
        optional.push('na');
      }
    }
  }
  const objectTypeData = optional.length ? `:${optional.join(':')}` : '';

  return `${data.id}:${data.title.replace(/:/g, '|')}:${getAnalyticsObjectType({ data })}:${products.length ? products.map((p) => p.displayName).join('|') : 'unknown'}${objectTypeData}`;
};

export const triggerSearchResultClick = ({
  analyticsArea, data, href, env,
}) => {
  // eslint-disable-next-line max-len
  const linkVal = `${analyticsArea}:${generateCourseDataAnalyticsString({ data })}`;
  const linkHref = href?.startsWith('/') ? `${window.location.origin}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}${href}` : href;
  if (window?.analytics?.callback?.searchResultLinkClick) {
    window.analytics.callback.searchResultLinkClick(linkVal, 1, linkHref);
  }

  const linkClickEvent = getCjsLinkClickEventForSearchResult({
    analyticsArea,
    href: linkHref,
  });
  dispatchCJSCustomEvent({
    eventData: linkClickEvent,
    eventName: 'link_click',
    version: '1-0-0',
    eventContexts: mergeCjsEventContexts(
      getPageContext(),
      getContentContext({
        contentId: data?.id,
        env,
        versionId: getContentVersionFromRecord(data),
      }),
    ),
  });

  const learnOneEvent = getCjsLearnOneEventForSearchResult({
    data,
  });
  dispatchCJSCustomEvent({
    eventData: learnOneEvent,
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({ record: data, env }),
  });
};

const formatSearchResultData = ({ result }) => {
  const retData = {
    activeFilters: [],
  };

  const resGroupFilters = result?.filterGroups?.map(
    (fg) => (fg.filters?.map(
      (filter) => filter?.selections?.filter(
        (selection) => selection.selected,
      ).map(
        (selection) => ({ filterCategory: filter.label, filterName: selection.text }),
      ),
    ).flat() || []),
  ).flat() || [];

  const resFilters = result?.filters?.map(
    (filter) => filter?.selections?.filter(
      (selection) => selection.selected,
    ).map(
      (selection) => ({ filterCategory: filter.label, filterName: selection.text }),
    ),
  ).flat() || [];
  if (resGroupFilters && resGroupFilters.length) {
    retData.activeFilters.push(...resGroupFilters);
  }

  if (resFilters && resFilters.length) {
    retData.activeFilters.push(...resFilters);
  }

  if (result.term) {
    retData.searchTerm = result.term;
  }

  if (retData.activeFilters.length === 0) {
    delete retData.activeFilters;
  }

  return retData;
};

export const triggerInternalSearch = ({ result }) => {
  const searchResultData = formatSearchResultData({ result });
  if (Object.prototype.hasOwnProperty.call(result, 'totalResults')) {
    const event = {
      eventData: {
        eventName: 'internal_search',
        values: {
          ...searchResultData,
          searchType: 'learn one catalog page',
          searchResultsCount: result.totalResults,
        },
      },
    };

    pushEvent({ event });

    const searchEvent = getCjsSearchEvent({ result, actionType: 'search_initiated' });
    dispatchCJSCustomEvent({
      eventData: searchEvent,
      eventName: 'search',
      version: '1-1-0',
      eventContexts: getPageContext(),
    });
  }
};

export const triggerSearchRefinement = ({ result }) => {
  const searchResultData = formatSearchResultData({ result });
  if (searchResultData.activeFilters) {
    const event = {
      eventData: {
        eventName: 'search_refinement',
        values: {
          ...searchResultData,
        },
      },
    };

    pushEvent({ event });

    const searchEvent = getCjsSearchEvent({ result, actionType: 'search_refinement' });
    dispatchCJSCustomEvent({
      eventData: searchEvent,
      eventName: 'search',
      version: '1-1-0',
      eventContexts: getPageContext(),
    });
  }
};

export const generateCourseInfo = ({ isComplete = null, courseData }) => {
  const products = courseData?.meta?.products || null;
  const role = courseData?.meta?.discipline || null;
  const courseType = courseData?.meta?.type || null;

  const courseInfo = {
    productLineCode: Array.isArray(products) ? products.map((prod) => prod.guid) : [],
    role: [role],
    courseId: courseData?.id,
    courseName: formatString(courseData?.title),
    courseType,
  };

  if (isComplete !== null) {
    courseInfo.alreadyCompleted = isComplete; // Is the unit already completed (revisiting the unit)
  }

  return courseInfo;
};

export const generateModuleInfo = ({
  moduleId, courseData, moduleData, verb,
}) => {
  if (courseData) {
    const moduleIndex = courseData?.tutorials?.findIndex((module) => module.id === moduleId);
    if (moduleIndex > -1) {
      const retData = {
        moduleName: formatString(courseData.tutorials[moduleIndex].title),
        moduleStep: moduleIndex + 1,
        moduleType: courseData.tutorials[moduleIndex].isStandAlone ? 'StandAlone' : 'Contextual',
        moduleCategory: courseData.tutorials[moduleIndex].moduleType,
        moduleId: courseData.tutorials[moduleIndex].id,
      };

      if (verb) {
        retData.status = verb;
      }

      return retData;
    }
  }

  if (moduleData) {
    const retData = {
      moduleName: formatString(moduleData.title),
      moduleStep: 1,
      moduleType: moduleData.isStandAlone ? 'StandAlone' : 'Contextual',
      moduleCategory: moduleData?.meta?.type,
      moduleId: moduleData.id,
    };

    if (verb) {
      retData.status = verb;
    }

    return retData;
  }

  return {};
};

export const triggerCourseStatusChange = ({
  courseData, status, learningRecords, env,
}) => {
  const statusMap = { };
  statusMap[AA_EVENT_START] = LRS_VERB_STARTED;
  statusMap[AA_EVENT_COMPLETE] = LRS_VERB_COMPLETED;

  if (courseData) {
    const courseRecord = learningRecords ? learningRecords[generateLRSID({ id: courseData.id, env })] : { };
    const statusExists = courseRecord && courseRecord.statements && (status === AA_EVENT_START || status === AA_EVENT_COMPLETE)
      ? courseRecord.statements.filter(
        (statement) => statement.verb === statusMap[status],
      ).length > 0
      : false;
    const alreadyComplete = status === AA_EVENT_CONTINUE && LRSHasVerbRecordForID({
      id: courseData.id, learningRecords, verb: LRS_VERB_COMPLETED, env,
    });
    const isCourseCompleted = learningRecords && env ? hasCompletedRecord({ learningRecords, id: courseData.id, env }) : false;

    if (statusExists || alreadyComplete) { // Short-cut - don't retrigger event for existing start/complete status or continue if already completed
      return;
    }

    if (status === AA_EVENT_CONTINUE) {
      if (!sessionAnalyticsHasValue({ key: AA_SESSION_KEY_COURSE_CONTINUE, value: courseData.id })) { // Doesn't have course continue in session
        addSessionAnalyticsValue({ key: AA_SESSION_KEY_COURSE_CONTINUE, value: courseData.id });
      } else {
        return; // Do not log a continue more than once per session
      }
    }

    const event = {
      event: {
        eventType: 'course-status-change',
      },
      courseInfo: generateCourseInfo({ courseData }),
    };

    event.courseEvent = { courseEventType: status };

    pushEvent({ event });

    const learnOneEvent = getCjsLearnOneEventForCourseStatusChange({ courseData, status, alreadyCompleted: isCourseCompleted });
    dispatchCJSCustomEvent({
      eventData: learnOneEvent,
      eventName: 'learn',
      eventContexts: buildLearnEventContexts({ record: courseData, env }),
    });
  } else {
    logger.error('Failed to push course status change event');
  }
};

export const triggerModuleEventCourseStatusChange = ({
  courseData, status, learningRecords, env,
}) => {
  if (status === AA_EVENT_START) {
    if (!sessionAnalyticsHasValue({ key: AA_SESSION_KEY_COURSE_CONTINUE, value: courseData.id })) { // Doesn't have course continue in session
      const completedModules = courseData?.tutorials?.filter((module) => LRSHasVerbRecordForID({
        learningRecords, id: module.id, env, verb: LRS_VERB_COMPLETED,
      })) || [];
      if (completedModules.length) { // At least one module has a completed record
        triggerCourseStatusChange({
          courseData, status: AA_EVENT_CONTINUE, learningRecords, env,
        });
        addSessionAnalyticsValue({ key: AA_SESSION_KEY_COURSE_CONTINUE, value: courseData.id });
      }
    }
  }

  if (status === AA_EVENT_REVISIT || status === AA_EVENT_CONTINUE) {
    if (LRSHasVerbRecordForID({
      learningRecords, id: courseData.id, env, verb: LRS_VERB_COMPLETED,
    })) { // Has LRS complete record
      if (!sessionAnalyticsHasValue({ key: AA_SESSION_KEY_COURSE_REVISIT, value: courseData.id })) { // Doesn't have course revisit in session
        triggerCourseStatusChange({
          courseData, status: AA_EVENT_REVISIT, learningRecords, env,
        });
        addSessionAnalyticsValue({ key: AA_SESSION_KEY_COURSE_REVISIT, value: courseData.id });
      }
    } else if (!sessionAnalyticsHasValue({ key: AA_SESSION_KEY_COURSE_CONTINUE, value: courseData.id })) { // Doesn't have LRS complete or course continue in session
      triggerCourseStatusChange({
        courseData, status: AA_EVENT_CONTINUE, learningRecords, env,
      });
      addSessionAnalyticsValue({ key: AA_SESSION_KEY_COURSE_CONTINUE, value: courseData.id });
    }
  }
};

export const triggerModuleStatusChange = ({
  courseData, moduleId, status, learningRecords, env, moduleData,
}) => {
  const statusMap = { };
  statusMap[AA_EVENT_START] = LRS_VERB_STARTED;
  statusMap[AA_EVENT_COMPLETE] = LRS_VERB_COMPLETED;

  const hasFiredMap = {};
  hasFiredMap[AA_EVENT_CONTINUE] = AA_SESSION_KEY_MODULE_CONTINUE;
  hasFiredMap[AA_EVENT_REVISIT] = AA_SESSION_KEY_MODULE_REVISIT;

  if (courseData || moduleData) {
    const statusExists = status === AA_EVENT_START || status === AA_EVENT_COMPLETE
      ? LRSHasVerbRecordForID({
        learningRecords, id: moduleId, env, verb: statusMap[status],
      })
      : false;
    const hasFired = status === AA_EVENT_CONTINUE || status === AA_EVENT_REVISIT
      ? sessionAnalyticsHasValue({ key: hasFiredMap[status], value: moduleId })
      : false;

    if (statusExists || hasFired) { // Short-cut - don't retrigger event for existing start/complete status or if continue/revisit in session
      if (statusExists && status === AA_EVENT_COMPLETE) { // Complete already exists trigger revisit
        triggerModuleStatusChange({
          courseData, moduleId, status: AA_EVENT_REVISIT, learningRecords, env, moduleData,
        });
      }
      return;
    }

    if (courseData && status === AA_EVENT_CONTINUE && !LRSHasVerbRecordForID({
      learningRecords, id: moduleId, env, verb: LRS_VERB_STARTED,
    })) { // Short-cut do not fire module continue if start record doesn't exist.
      // Do trigger course continue
      triggerModuleEventCourseStatusChange({
        courseData, status, learningRecords, env,
      });
      return;
    }

    const event = {
      event: {
        eventType: 'module-status-change',
      },
      moduleInfo: generateModuleInfo({
        moduleId, courseData, verb: status, moduleData,
      }),
    };

    if (courseData) {
      event.courseInfo = generateCourseInfo({ courseData });
    }

    if (status === AA_EVENT_CONTINUE || status === AA_EVENT_REVISIT) {
      addSessionAnalyticsValue({ key: hasFiredMap[status], value: moduleId });
    }

    pushEvent({ event });

    const isModuleCompleted = learningRecords && env ? hasCompletedRecord({ learningRecords, id: moduleId, env }) : false;
    const moduleEvent = getCjsLearnOneEventForModuleStatusChange({
      moduleId,
      moduleData,
      courseData,
      status,
      alreadyCompleted: isModuleCompleted,
    });
    dispatchCJSCustomEvent({
      eventData: moduleEvent,
      eventName: 'learn',
      eventContexts: buildLearnEventContexts({
        record: resolveModuleContentRecord({ courseData, moduleData, moduleId }),
        env,
      }),
    });

    if (courseData) {
      triggerModuleEventCourseStatusChange({
        courseData,
        status,
        learningRecords,
        env,
      });
    }
  } else {
    logger.error('Failed to push module status change event');
  }
};

export const triggerErrorBanner = ({
  errorLocation = 'page', errorType = 'unknown', errorCode = 'unknown', errorReason = 'unknown', errorMessage = 'unknown',
}) => {
  const event = {
    eventData: {
      eventName: 'page_error',
      values: {
        errors: [
          {
            errorLocation,
            errorType,
            errorCode,
            errorReason,
            errorMessage,
          },
        ],
      },
    },
  };
  pushEvent({ event });

  const errorContext = getCjsErrorContextForErrorBanner({
    errorLocation, errorType, errorCode, errorReason, errorMessage,
  });
  dispatchCJSCustomEvent({
    eventData: { action_type: 'page_error' },
    eventName: 'learn',
    eventContexts: { ...errorContext, ...getPageContext() },
  });
};

export const triggerSearchBarClick = () => {
  const event = {
    event: {
      eventType: 'link_click',
    },
    link: {
      linkTitle: 'search bar',
    },
  };

  pushEvent({ event });

  const linkClickEvent = getCjsLinkClickEventForSearchBar({ linkTitle: 'search bar' });
  dispatchCJSCustomEvent({
    eventData: linkClickEvent,
    eventName: 'link_click',
    version: '1-0-0',
    eventContexts: getPageContext(),
  });
};

export const triggerNotificationImpression = ({ notificationType = 'unknown', notificationReason = 'unknown' }) => {
  const event = {
    eventData: {
      eventName: 'notification_impression',
      values: {
        notifications: [{
          notificationOrigin: 'CREDSYS',
          notificationType,
          notificationReason: notificationReason.substr(0, 80), // Keep to total of < 100 chars
        }],
      },
    },
  };
  pushEvent({ event });

  const notificationEvent = getCjsNotificationEventForNotificationImpression({ notificationType, notificationReason });
  dispatchCJSCustomEvent({
    eventData: notificationEvent,
    eventName: 'notification',
    version: '1-0-2',
    eventContexts: getPageContext(),
  });
};

export const triggerContentImpression = ({
  containerType, containerName, containerLocation, containerSegment, cssSelector,
}) => {
  const event = {
    eventData: {
      eventName: 'content-viewed',
      values: {
        contentContainer: [{
          containerType,
          containerName,
          containerLocation,
          containerSegment,
        }],
      },
    },
  };

  if (cssSelector) {
    event.eventData.values = {
      ...event.eventData.values,
      cssSelector,
    };
  }

  pushEvent({ event });

  const contentEvent = getCjsLearnOneEventForContentImpression({
    containerType, containerName, containerLocation, containerSegment, cssSelector,
  });
  dispatchCJSCustomEvent({
    eventData: contentEvent,
    eventName: 'impression',
    version: '1-0-0',
    eventContexts: getPageContext(event, 'content-impression'),
  });
};

export const generateUnitInfo = ({
  unitId, courseData, moduleData, isUnitComplete = null,
}) => {
  const outline = courseData || moduleData;
  const unit = outline.unitLookup[unitId];
  const parentArray = courseData ? courseData.tutorials.filter((mod) => mod.id === unit.parentId) : [moduleData];

  const unitInfo = { // if there is a unit, else skip this part
    //    section: blocks[gparentId] ? formatString(blocks[gparentId].display_name) : gparentId,
    subSection: parentArray.length ? formatString(parentArray[0].title) : unit?.parentId,
    unitId,
    unitName: formatString(unit?.title),
  };

  if (isUnitComplete !== null) {
    unitInfo.alreadyCompleted = isUnitComplete; // Is the unit already completed (revisiting the unit)
  }

  return unitInfo;
};

export const triggerProblemResult = ({
  courseData, unitId, problemInfo, moduleData, isUnitComplete, env,
}) => {
  if ((courseData || moduleData) && unitId && problemInfo) {
    const event = {
      event: {
        eventType: 'problem-result',
      },
      unitInfo: generateUnitInfo({
        unitId, courseData, moduleData, isUnitComplete,
      }),
      problemInfo,
    };

    if (courseData) {
      event.courseInfo = generateCourseInfo({ courseData });
    }

    if (moduleData) {
      event.moduleInfo = generateModuleInfo({ moduleData });
    }

    pushEvent({ event });

    const problemEvent = getCjsLearnOneEventForProblemResult({
      courseData, unitId, problemInfo, moduleData, isUnitComplete,
    });
    dispatchCJSCustomEvent({
      eventData: problemEvent,
      eventName: 'learn',
      eventContexts: buildLearnEventContexts({
        record: resolveUnitContentRecord({ courseData, moduleData, unitId }),
        env,
      }),
    });
  } else {
    logger.error('Failed to push problem result event');
  }
};

export const triggerUnitComplete = ({
  learningRecords, courseData, unitId, env, moduleData,
}) => {
  const outline = courseData || moduleData;
  if (outline && unitId && outline.unitLookup && outline.unitLookup[unitId]) {
    const completed = unitHasCompleteRecord({
      learningRecords, id: unitId, env,
    });
    const moduleId = courseData ? outline.unitLookup[unitId].parentId : undefined;
    // Don't retrigger unit complete for already completed unit
    if (!completed) {
      const event = {
        event: {
          eventType: 'unit-complete',
        },
        unitInfo: generateUnitInfo({ unitId, courseData, moduleData }),
      };

      if (courseData) {
        event.courseInfo = generateCourseInfo({ courseData, isComplete: hasCompletedRecord({ learningRecords, id: courseData.id, env }) });
      }

      if (moduleData) {
        event.moduleInfo = generateModuleInfo({
          courseData, moduleData, verb: LRS_VERB_COMPLETED, moduleId,
        });
      }

      pushEvent({ event });

      const isCourseCompleted = courseData && learningRecords && env ? hasCompletedRecord({ learningRecords, id: courseData.id, env }) : false;
      const unitCompleteEvent = getCjsLearnOneEventForUnitComplete({
        courseData, unitId, moduleData, alreadyCompleted: isCourseCompleted,
      });
      dispatchCJSCustomEvent({
        eventData: unitCompleteEvent,
        eventName: 'learn',
        eventContexts: buildLearnEventContexts({
          record: resolveUnitContentRecord({ courseData, moduleData, unitId }),
          env,
        }),
      });
    }

    // Trigger course revisit if course is complete
    if (courseData && LRSHasVerbRecordForID({
      learningRecords, id: courseData.id, env, verb: LRS_VERB_COMPLETED,
    })) {
      triggerCourseStatusChange({
        courseData, status: AA_EVENT_REVISIT, learningRecords, env,
      });
    }
  } else {
    logger.error('Failed to push unit complete event');
  }
};

export const triggerCoursePageView = ({
  courseData, unitId, isComplete, isUnitComplete, env,
}) => {
  if (courseData) {
    const event = {
      event: {
        eventType: 'course-pageview',
      },
      courseInfo: generateCourseInfo({ isComplete, courseData }),
    };

    let pageName = `adsk:en:learn:course:${formatString(event.courseInfo.courseName)}`;

    if (unitId) {
      event.unitInfo = generateUnitInfo({ unitId, courseData, isUnitComplete });
      pageName += `:${formatString(event.unitInfo.unitName)}`;
    }

    setChildProperty(pageName, 'digitalData.page.pageName', window);

    pushEvent({ event });

    const coursePageViewEvent = getCjsLearnOneEventForCoursePageView({
      courseData, unitId, isComplete, isUnitComplete,
    });
    dispatchCJSCustomEvent({
      eventData: coursePageViewEvent,
      eventName: 'learn',
      eventContexts: buildLearnEventContexts({
        record: courseData,
        env,
        pageCategory: 'course',
        event,
      }),
    });
  } else {
    logger.error('Failed to push pageview event');
  }
};

export const triggerModulePageView = ({
  pageName, courseData, moduleData, moduleId, unitId, isUnitComplete, env,
}) => {
  if (pageName) {
    setChildProperty(`adsk:en:learn:${pageName}`, 'digitalData.page.pageName', window);
  } else if (window?.location.pathname) {
    setChildProperty(`adsk:en${window.location.pathname.replace(/\//g, ':')}`, 'digitalData.page.pageName', window);
  }

  const event = {
    eventData: {
      eventName: 'new_page_view',
    },
  };

  event.eventData.values = {
    pageName: window.digitalData.page.pageName,
  };

  if (courseData) {
    event.courseInfo = generateCourseInfo({ courseData });
    event.moduleInfo = generateModuleInfo({ moduleId, courseData });
  }

  if (moduleData) {
    event.moduleInfo = generateModuleInfo({ moduleData });
  }

  if (unitId) {
    event.unitInfo = generateUnitInfo({
      unitId, courseData, moduleData, isUnitComplete,
    });
  }

  pushEvent({ event });

  const modulePageViewEvent = getCjsLearnOneEventForModulePageView({
    pageName, courseData, moduleData, moduleId, unitId, isUnitComplete,
  });
  dispatchCJSCustomEvent({
    eventData: modulePageViewEvent,
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({
      record: resolveModuleContentRecord({ courseData, moduleData, moduleId: moduleId || moduleData?.id }),
      env,
      pageCategory: 'module',
      event,
    }),
  });
};

export const triggerUnitConsume = ({
  courseData, moduleData, unitId, learningRecords, env,
}) => {
  const event = {
    event: {
      eventType: 'unit-consume',
    },
  };

  if (courseData) {
    event.courseInfo = generateCourseInfo({ courseData, isComplete: hasCompletedRecord({ learningRecords, id: courseData.id, env }) });
  }

  if (moduleData) {
    event.moduleInfo = generateModuleInfo({ moduleData });
  }

  if (unitId) {
    event.unitInfo = generateUnitInfo({
      unitId, courseData, moduleData,
    });
  }

  pushEvent({ event });

  const isCourseCompleted = courseData && learningRecords && env ? hasCompletedRecord({ learningRecords, id: courseData.id, env }) : false;
  const unitConsumeEvent = getCjsLearnOneEventForUnitConsume({
    courseData, moduleData, unitId, alreadyCompleted: isCourseCompleted,
  });
  dispatchCJSCustomEvent({
    eventData: unitConsumeEvent,
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({
      record: resolveUnitContentRecord({ courseData, moduleData, unitId }),
      env,
    }),
  });
};

// Learning Plan Analytics Functions
export const triggerLearningPlanPageView = ({ learningPlanDetail, learningPlanId }) => {
  let pageName = `adsk:en:learn:learning-plan:${learningPlanId}`;
  const isFastTrack = isFastTrackLearningPlan(learningPlanDetail);
  if (isFastTrack) {
    pageName = `adsk:en:learn:my-dashboard:learning-plan:fast-track:${formatString(learningPlanDetail.title)}:${learningPlanDetail.id}`;
  }
  // Set the page name in digitalData
  setChildProperty(pageName, 'digitalData.page.pageName', window);

  const event = {
    eventData: {
      eventName: 'learning_plan_page_view',
      values: {
        pageName,
      },
    },
    events: ['event1'],
    eVars: {
      v24: pageName,
    },
  };

  pushEvent({ event });

  dispatchCJSCustomEvent({
    eventData: {},
    eventName: 'page_view',
    version: '1-0-0',
    eventContexts: getPageContext(event, 'learning-plan'),
  });
};

export const generateLearningPlanContentAnalyticsString = ({
  data, learningRecords, env, action = 'title', isItemFromFastTrackLearningPlan = false,
}) => {
  // Extract contentful ID from various possible sources
  let contentfulId = '';
  if (data.id) {
    contentfulId = data.id;
  } else if (data.externalId) {
    // Extract ID from contentful URL
    const matches = data.externalId.match(/entries\/([^/?]+)/);
    contentfulId = matches ? matches[1] : data.externalId;
  }

  // Get content name (title) and validate
  let contentName = 'unknown';
  if (data.title && typeof data.title === 'string' && data.title.trim().length > 0) {
    contentName = data.title.replace(/:/g, '|');
  }

  // Get content type based on objectType and learningMode
  let contentType = 'unknown';
  switch (data?.objectType?.toLowerCase().replace(' ', '')) {
  case 'module':
    contentType = data.learningMode === LEARNING_MODE_COURSEWARE ? 'module' : 'tutorial';
    break;
  case 'course':
    contentType = data.learningMode === LEARNING_MODE_COURSEWARE ? 'course' : 'curated-list';
    break;
  case 'learningpathway':
    contentType = 'collection';
    break;
  default:
    contentType = 'unknown';
  }

  // Get products
  let products = [];
  if (data?.products) {
    products = data.products;
  } else if (data?.meta?.products) {
    products = data.meta.products;
  }
  const productString = products.length ? products.map((p) => p.displayName).join('|') : 'unknown';

  // Get status based on learning records
  let status = 'not-started';
  if (learningRecords && env) {
    if (hasCompletedRecord({ learningRecords, id: data.id, env })) {
      status = 'completed';
    } else if (hasConsumedRecord({ learningRecords, id: data.id, env })) {
      status = 'consumed';
    } else if (hasStartedRecord({ learningRecords, id: data.id, env })) {
      status = 'started';
    }
  }

  if (isItemFromFastTrackLearningPlan) {
    return `content > my-dashboard:learning-plan:fast-track:${action}:${contentfulId}:${formatString(contentName)}:${contentType}:${productString}:${status}`;
  }
  return `content > learning-plan:${action}:${contentfulId}:${contentName}:${contentType}:${productString}:${status}`;
};

export const triggerLearningPlanContentClick = ({
  data, learningRecords, env, isItemFromFastTrackLearningPlan = false,
}) => {
  const evarValue = generateLearningPlanContentAnalyticsString({
    data, learningRecords, env, action: 'title', isItemFromFastTrackLearningPlan,
  });

  const event = {
    eventData: {
      eventName: 'learning_plan_content_click',
      values: {
        contentAction: 'title_click',
      },
    },
    events: ['event21'],
    eVars: {
      evar21: evarValue,
    },
  };

  pushEvent({ event });

  const status = getLearningStatus({ learningRecords, id: data.id, env });
  const learningPlanContentClickEvent = getCjsLearnOneEventForLearningPlanAction({ data, status, actionType: 'learning_plan_content_click' });
  dispatchCJSCustomEvent({
    eventData: learningPlanContentClickEvent,
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({
      record: data,
      env,
      pageCategory: 'learning-plan',
      event,
    }),
  });
};

export const triggerLearningPlanAddToList = ({
  data, learningRecords, env,
}) => {
  const evarValue = generateLearningPlanContentAnalyticsString({
    data, learningRecords, env, action: 'add-to-list',
  });

  const event = {
    eventData: {
      eventName: 'learning_plan_add_to_list',
      values: {
        contentAction: 'add_to_list',
      },
    },
    events: ['event21'],
    eVars: {
      evar21: evarValue,
    },
  };

  pushEvent({ event });

  const status = getLearningStatus({ learningRecords, id: data.id, env });
  const learningPlanAddToListEvent = getCjsLearnOneEventForLearningPlanAction({ data, status, actionType: 'learning_plan_add_to_list' });
  dispatchCJSCustomEvent({
    eventData: learningPlanAddToListEvent,
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({
      record: data,
      env,
      pageCategory: 'learning-plan',
      event,
    }),
  });
};

export const triggerLearningPlanBookmark = ({
  data, learningRecords, env, isBookmarked,
}) => {
  const action = isBookmarked ? 'remove-from-bookmarks' : 'add-to-bookmarks';
  const evarValue = generateLearningPlanContentAnalyticsString({
    data, learningRecords, env, action,
  });

  const event = {
    eventData: {
      eventName: 'learning_plan_bookmark',
      values: {
        contentAction: action,
      },
    },
    events: ['event21'],
    eVars: {
      evar21: evarValue,
    },
  };

  pushEvent({ event });

  const status = getLearningStatus({ learningRecords, id: data.id, env });
  const learningPlanBookmarkEvent = getCjsLearnOneEventForLearningPlanAction({
    data, status, actionType: 'learning_plan_bookmark', isBookmarked,
  });
  dispatchCJSCustomEvent({
    eventData: learningPlanBookmarkEvent,
    eventName: 'learn',
    eventContexts: buildLearnEventContexts({
      record: data,
      env,
      pageCategory: 'learning-plan',
      event,
    }),
  });
};

export const triggerInventorBannerCtaClick = ({ ctaUrl }) => {
  const clickEvent = getCjsInventorBannerCtaClick({ ctaUrl });
  dispatchCJSCustomEvent({
    eventData: clickEvent,
    eventName: 'link_click',
    version: '1-0-0',
    eventContexts: getPageContext(null, 'inventor-banner-click'),
  });
};

export const triggerInventorExperimentExposure = ({ experimentId, groupName }) => {
  const eventData = getCjsInventorExperimentExposure();
  const pageContext = getPageContext(null, 'inventor-cta-banner');
  const experimentContextData = getCjsInventorExperimentContext({ experimentId, groupName });
  dispatchCJSCustomEvent({
    eventData,
    eventName: 'user_experiment_exposure',
    version: '1-0-0',
    eventContexts: {
      ...pageContext,
      experiment_context: { version: '1-2-0', data: experimentContextData },
    },
  });
};

// Product-agnostic CTA banner experiment events (see ctaBannerExperiments.js).
// The experiment / variant context is the same shape as the Inventor exposure.
export const triggerCtaBannerExposure = ({ product, experimentId, groupName }) => {
  dispatchCJSCustomEvent({
    eventData: getCjsInventorExperimentExposure(),
    eventName: 'user_experiment_exposure',
    version: '1-0-0',
    eventContexts: {
      ...getPageContext(null, `${product}-cta-banner`),
      experiment_context: { version: '1-2-0', data: getCjsInventorExperimentContext({ experimentId, groupName }) },
    },
  });
};

// Unlike the Inventor click, the click also carries the experiment context so clicks can be
// attributed to a variant without joining them to exposure events.
export const triggerCtaBannerClick = ({
  product, ctaUrl, experimentId, groupName,
}) => {
  dispatchCJSCustomEvent({
    eventData: getCjsCtaBannerClick({ product, ctaUrl }),
    eventName: 'link_click',
    version: '1-0-0',
    eventContexts: {
      ...getPageContext(null, `${product}-banner-click`),
      experiment_context: { version: '1-2-0', data: getCjsInventorExperimentContext({ experimentId, groupName }) },
    },
  });
};
